import React, { useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import SnoozeIcon from '@mui/icons-material/Snooze';

const SPEED_OPTIONS = [
  {
    icon: PlayArrowIcon,
    label: 'Normal',
    description: 'Play at the regular speaking speed.',
    playbackRate: 1.0,
  },
  {
    icon: SlowMotionVideoIcon,
    label: 'Slow',
    description: 'A little slower for easier listening.',
    playbackRate: 0.75,
  },
  {
    icon: SnoozeIcon,
    label: 'Very slow',
    description: 'The slowest autoplay playback speed.',
    playbackRate: 0.5,
  },
];

function PlaybackSpeedControls1({
  playbackRate = 1.0,
  setPlaybackRate,
  triggerMode = 'button',
  triggerLabel = 'Playback speed',
  onTriggerClick,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedOption =
    SPEED_OPTIONS.find((option) => option.playbackRate === Number(playbackRate))
    || SPEED_OPTIONS[0];

  const handleTriggerClick = (event) => {
    if (onTriggerClick) {
      onTriggerClick(event);
    }
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleSelect = (option) => {
    setPlaybackRate(option.playbackRate);
    setAnchorEl(null);
  };

  const trigger =
    triggerMode === 'menuItem' ? (
      <MenuItem onClick={handleTriggerClick}>
        <ListItemIcon>
          <GraphicEqIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={triggerLabel}
          secondary={selectedOption.label}
          secondaryTypographyProps={{ fontSize: '0.78rem' }}
        />
      </MenuItem>
    ) : (
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<GraphicEqIcon fontSize="small" />}
        onClick={handleTriggerClick}
        sx={{
          minWidth: 160,
          justifyContent: 'flex-start',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        Playback speed: {selectedOption.label}
      </Button>
    );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {trigger}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper
            elevation={3}
            sx={{
              width: 280,
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                px: 1.5,
                pt: 1.25,
                pb: 0.5,
                color: 'text.secondary',
                fontWeight: 700,
              }}
            >
              Playback speed
            </Typography>
            <List dense disablePadding>
              {SPEED_OPTIONS.map((option) => {
                const selected = option.label === selectedOption.label;
                const OptionIcon = option.icon;

                return (
                  <ListItemButton
                    key={option.label}
                    selected={selected}
                    onClick={() => handleSelect(option)}
                    sx={{ alignItems: 'flex-start', py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, pt: 0.35 }}>
                      <OptionIcon color={selected ? 'primary' : 'action'} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={option.label}
                      secondary={option.description}
                      primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

export default PlaybackSpeedControls1;
